import { FormatType } from "../../../models/EbayOffer";
import { IPart } from "../../../models/Part";
import checkEbayDisabled from "../../../utils/checkEbayDisabled";
import { computeEbayListingTitle } from "../EbayListingTitle";

export const EBAY_TITLE_MAX = 80;

export type ListingWorkflowStep = {
  id: string;
  label: string;
  done: boolean;
  /** e.g. blockers when "Ready to list" is not passable (from `checkEbayDisabled`). */
  detail?: string;
};

const WORKFLOW_OVERRIDE_STORAGE_KEY = "unlistedListingWorkflowOverrides_v1";

export function loadWorkflowOverrides(
  partId: string
): Record<string, boolean> {
  if (typeof window === "undefined") return {};
  try {
    const raw = localStorage.getItem(WORKFLOW_OVERRIDE_STORAGE_KEY);
    if (!raw) return {};
    const all = JSON.parse(raw) as Record<string, Record<string, boolean>>;
    return all[partId] ? { ...all[partId] } : {};
  } catch {
    return {};
  }
}

export function saveWorkflowOverrides(
  partId: string,
  overrides: Record<string, boolean>
) {
  if (typeof window === "undefined") return;
  try {
    const raw = localStorage.getItem(WORKFLOW_OVERRIDE_STORAGE_KEY);
    const all: Record<string, Record<string, boolean>> = raw
      ? JSON.parse(raw)
      : {};
    if (Object.keys(overrides).length === 0) {
      delete all[partId];
    } else {
      all[partId] = { ...overrides };
    }
    localStorage.setItem(
      WORKFLOW_OVERRIDE_STORAGE_KEY,
      JSON.stringify(all)
    );
  } catch {
    // ignore quota / private mode
  }
}

function activeImagesCount(part: IPart): number {
  return (part.images ?? []).filter(
    (i) => (i.url || i.thumbnailUrl) && !i.deleted
  ).length;
}

/**
 * Heuristic checks for the unlisted listing prep pipeline (no new API fields).
 *
 * Auto-validation rules:
 * - Title: configured when the (merged) title is non-empty and <= 80 chars.
 * - Images: done as soon as at least one active image is attached.
 * - Price: done when an entered price or a live offer price is set.
 * - MPN: done when an OE/part number is present.
 * - Specifics: done when every aspect (required AND optional) has a value.
 */
export function getListingWorkflowSteps(part: IPart): ListingWorkflowStep[] {
  const offer = part.offers?.[0];

  const mergedTitle = (computeEbayListingTitle(part) ?? "").trim();
  const offerTitle = offer?.title?.trim() ?? "";
  const effectiveTitle = mergedTitle || offerTitle;
  const titleLen = effectiveTitle.length;
  const titleConfigured = titleLen > 0 && titleLen <= EBAY_TITLE_MAX;
  const titleDetail =
    titleLen === 0
      ? "No title set"
      : titleLen > EBAY_TITLE_MAX
        ? `Title is ${titleLen}/${EBAY_TITLE_MAX} chars — shorten to fit`
        : undefined;

  const hasImage = activeImagesCount(part) > 0;

  const offerPrice = offer
    ? offer.format === FormatType.Auction
      ? offer.auctionStartPrice
      : offer.buyItNowPrice
    : null;
  const hasEntered =
    part.enteredPrice != null && Number(part.enteredPrice) > 0;
  const hasOfferPrice = offerPrice != null && Number(offerPrice) > 0;
  const priceChecked = hasEntered || hasOfferPrice;

  const mpn = (part.oePartNumber ?? part.partNumber ?? "").trim();
  const mpnAdded = mpn.length > 0;

  const aspects = part.aspects ?? [];
  const missingAspects = aspects.filter(
    (a) => !((a.value ?? "").trim().length > 0)
  );
  const specificsCompleted =
    aspects.length > 0 && missingAspects.length === 0;
  const specificsDetail =
    aspects.length === 0
      ? "No aspects loaded"
      : missingAspects.length > 0
        ? `${missingAspects.length} aspect${
            missingAspects.length === 1 ? "" : "s"
          } missing — includes optionals`
        : undefined;

  const partCategoryDefault = part.category?.partCategoryDefaults?.[0] ?? null;
  const readyCheck = checkEbayDisabled({
    part,
    offer: offer ?? null,
    partCategoryDefault,
    ignoreListingTime: true,
  });
  const readyToList = !readyCheck.isDisabled;
  const readyDetail =
    readyCheck.isDisabled && readyCheck.error ? readyCheck.error : undefined;

  return [
    {
      id: "title",
      label: "Title Configured",
      done: titleConfigured,
      detail: titleDetail,
    },
    { id: "images", label: "Images Checked", done: hasImage },
    { id: "price", label: "Price Checked", done: priceChecked },
    { id: "mpn", label: "MPN Added", done: mpnAdded },
    {
      id: "specifics",
      label: "Specifics Completed",
      done: specificsCompleted,
      detail: specificsDetail,
    },
    {
      id: "ready",
      label: "Ready to list",
      done: readyToList,
      detail: readyDetail,
    },
  ];
}
